import { Subject, takeUntil } from 'rxjs';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { PillComponent } from '@components';
import {
  ACTIVE_ERROR_TYPE,
  DashboardStore,
  DashboardTileType,
  InstallationType,
  KanbanBoardHelperService,
} from '@store';
import {
  AggregatedStatus,
  AnomalySeverity,
  DashboardAnomaly,
  DashboardDevice,
  DashboardInstallationData,
  DashboardSmartNote,
  FaultMessageSmartnotes,
  SubscriptionPlan,
} from '@vi-iot/client';
import { ViEllipsisDirective } from '@vi-iot/ui/directives/ellipsis/ellipsis.directive';
import { SearchService } from '../../../../../+search/search.service';
import { CustomTranslationsService } from '../../../../../operational-data-common/translations';
import { CommonStandaloneModule } from '../../../../../shared/common-standalone.module';
import { BookingPackageBadgeComponent } from '../../../../../shared/components/booking-package-badge/booking-package-badge.component';
import { PillType } from '../../../../../shared/components/pill/pill.model';
import { DashboardService } from '../../../dashboard.service';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { DashboardAnomalyPillPipe } from '../../../../../+kanban-board/kanban-installation-list/pipes/dashboard-anomaly-pill/dashboard-anomaly-pill.pipe';

@Component({
  selector: 'app-dashboard-tile-installation-list-item',
  templateUrl: './dashboard-tile-installation-list-item.component.html',
  styleUrls: ['./dashboard-tile-installation-list-item.component.scss'],
  standalone: true,
  imports: [
    forwardRef(() => CommonStandaloneModule),
    PillComponent,
    BookingPackageBadgeComponent,
    MatTooltipModule,
    ViEllipsisDirective,
    DashboardAnomalyPillPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardTileInstallationListItemComponent implements OnInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  @Input() installation: DashboardInstallationData;
  @Input() tileType: DashboardTileType;
  readonly pillType = PillType;
  readonly aggregatedStatus = AggregatedStatus;
  readonly tileTypes = DashboardTileType;
  readonly subscriptionPlan = SubscriptionPlan;
  readonly anomalySeverity = AnomalySeverity;
  readonly installationType = InstallationType;

  installationHeaderLabel: string;
  latestSmartNotes: FaultMessageSmartnotes;
  anomalies: DashboardAnomaly[] = [];
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public dashboardService: DashboardService,
    public searchService: SearchService,
    protected customTranslationsService: CustomTranslationsService,
    private kanbanBoardHelperService: KanbanBoardHelperService,
    private dashboardStore: DashboardStore
  ) {}

  ngOnInit(): void {
    this.installationHeaderLabel = this.kanbanBoardHelperService.getInstallationHeaderLabel(
      this.installation
    );
    if (this.tileType === DashboardTileType.InstallationsWithFault) {
      this.dashboardStore
        .getDashboardDevicesById(this.installation.id)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((devices) => {
          this.latestSmartNotes = devices
            ? this.dashboardService.getLatestSmartNotes(devices)
            : this.dashboardService.getLatestSmartNotes(this.installation.devices);
        });
    }

    if (
      this.tileType === DashboardTileType.InstallationsWithAnomalies &&
      this.installation.devices
    ) {
      this.installation.devices.forEach((device) => {
        device.anomalies.forEach((deviceAnomaly) => {
          if (this.anomalies.every((storedAnomaly) => storedAnomaly.id !== deviceAnomaly.id)) {
            this.anomalies.push(deviceAnomaly);
          }
        });
      });
    }
  }

  shouldShowFaultLabelWhenNoSmartNoteError(installation: DashboardInstallationData): boolean {
    const mainDevice = installation?.devices?.find(
      (device: DashboardDevice) => installation.device?.vin === device?.vin
    );
    const smartNoteError = !!mainDevice?.smartNotes?.some(
      (smartNote: DashboardSmartNote) => smartNote.type === ACTIVE_ERROR_TYPE.DEVICE_ERROR
    );

    if (smartNoteError || this.latestSmartNotes?.smartNotes.length > 0) {
      return false;
    }

    return installation.aggregatedStatus === AggregatedStatus.Error;
  }

  getStatusPill(anomaly: AnomalySeverity): string {
    switch (anomaly) {
      case AnomalySeverity.Critical:
        return PillType.errorInverted;
      case AnomalySeverity.Warning:
        return PillType.mediumInverted;
      case AnomalySeverity.WorkingFine:
        return PillType.successInverted;
      default:
      case AnomalySeverity.Information:
        return PillType.secondaryInverted;
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
